import { makeAutoObservable } from 'mobx';
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton, Spinner, Stack, StackItem } from '@fluentui/react';
import { observer } from 'mobx-react';
import { EntityAPI, IEntityTypeDefinition, IEntityColumn, IEntityToken, IEntityTypeDefinitionProps, EntityType, ISiteTemplate, IContentHubType, ISensitivityLabel } from '../../../API';
import { useEffect } from 'react';
import EntityTypeConnectionInfo from './tabs/EntityTypeConnectionInfo';
import { IUserConfigPackage } from '../../../API';
import ProvisionProfiles, { ProvisionProfilesState } from '../provision-profiles/ProvisionProfiles';
import EntityTypeTestDialog, { EntityTypeTestDialogState } from './EntityTypeTestDialog';
import WidgetOutline, { WidgetOutlineState } from '../../controls/WidgetOutline';
import EntityTypeSetUp from './tabs/EntityTypeSetUp';
import EntityTypeOutlookSettings from './tabs/EntityTypeOutlookSettings';

export class EntityTypeEditorState implements IEntityTypeDefinitionProps {
	
	public disabledTabs: string[] = ['OneDrive', 'Provisioning'];
	public listUrlColumns?: IEntityColumn[];
	public availableSiteTemplates?: ISiteTemplate[];
	public availableContentTypes?: IContentHubType[];
	public sensitivityLabels?: ISensitivityLabel[];
	public isLoadingList = false;
	public isSaving = false;

	public Id:string = '';
	public SingularName: string = '';
	public PluralName: string = '';
	public Type: EntityType = EntityType.None;
	public SharePointListUrl: string = '';
	public SharePointTemplateApprovals: boolean = false;
	public SharePointTemplateApprovalsListUrl: string = '';
	public SharePointStorageUrlField: string = '';
	public SharePointStorageUrlPattern: string = '';
	public ProvisionProfiles: string[] = [];
	public EnableWorkspaceSync: boolean = false;

	public OutlookFilingTagTemplate:string = '{{ Tag.IdBase36 }}';
	public OutlookIncludeInSearchResults:boolean = false;
	public OutlookEnableCreateNewOption:boolean = false;
	public OutlookEnableViewAllOption:boolean = false;

	public SharePointTemplateDocumentsLibraryUrl: string = '';
	
	public tokens?: IEntityToken[];
	public areTokensLoading = false;
	public isDeleting: boolean = false;
	public showDeleteConfirmationDialog: boolean = false;

	public testDialog:EntityTypeTestDialogState;
	public profiles: ProvisionProfilesState;
	userConfig: IUserConfigPackage;
	onDelete: () => void;
	public connectionInfoOutline: WidgetOutlineState = new WidgetOutlineState('Connection', '⚡', '#dbb744');
	public entityTypeSetupOutline: WidgetOutlineState = new WidgetOutlineState('Setup', '⚙️', '#382d09ff', false);
	public outlookSettingsOutline: WidgetOutlineState = new WidgetOutlineState('Outlook', '✉️', '#2e4affff', false);
	public testDialogOutline: WidgetOutlineState = new WidgetOutlineState('Test', '🧪', '#2e4affff', false);
	


	constructor(
		entityType:IEntityTypeDefinitionProps,
		userConfig:IUserConfigPackage,
		onDelete: () => void,
	) {
		this.onDelete = onDelete;
		this.Id = entityType.Id;
		this.SingularName = entityType.SingularName;
		this.PluralName = entityType.PluralName;
		
		this.SharePointStorageUrlField = entityType.SharePointStorageUrlField;
		this.SharePointStorageUrlPattern = entityType.SharePointStorageUrlPattern;
		this.SharePointTemplateDocumentsLibraryUrl = entityType.SharePointTemplateDocumentsLibraryUrl;
		this.EnableWorkspaceSync = entityType.EnableWorkspaceSync;

		this.OutlookFilingTagTemplate = entityType.OutlookFilingTagTemplate;
		this.OutlookIncludeInSearchResults = entityType.OutlookIncludeInSearchResults;
		this.OutlookEnableCreateNewOption = entityType.OutlookEnableCreateNewOption;
		this.OutlookEnableViewAllOption = entityType.OutlookEnableViewAllOption;
		this.SharePointTemplateApprovalsListUrl = entityType.SharePointTemplateApprovalsListUrl;
		this.SharePointTemplateApprovals = entityType.SharePointTemplateApprovals;

		this.Type = entityType.Type;

		this.userConfig = userConfig;
		
		this.profiles = new ProvisionProfilesState(this.userConfig, this, entityType.ProvisionProfiles || [], entityType.SharePointListUrl || '');
		this.testDialog = new EntityTypeTestDialogState(entityType, this.userConfig);

		this.setSharePointListUrl(entityType.SharePointListUrl || '');

		makeAutoObservable(this);
	}
	
	private async getApi() : Promise<EntityAPI<IEntityTypeDefinition>> {
		return await EntityAPI.fromEntityType<IEntityTypeDefinition>(
			EntityType.EntityTypeDefinition,
			this.userConfig,
		);
	}
	
	setWorkspaceSync(arg0: boolean) {
		this.EnableWorkspaceSync = arg0;
	}

	setSharePointTemplateApprovals(arg0: boolean) {
		this.SharePointTemplateApprovals = arg0;
	}

	setPluralName(PluralName: string): void {
		this.PluralName = PluralName;
	}
	setSingularName(s: string): void {
		this.SingularName = s;
	}
	setSharePointListUrl(arg0:string) {
		this.SharePointListUrl = arg0;
		this.profiles.setListUrl(arg0);
	}
	setStorageUrlField(arg0: string) {
		this.SharePointStorageUrlField = arg0;
	}
	setSharePointStorageUrlPattern(arg0: string) {
		this.SharePointStorageUrlPattern = arg0;
	}

	setSharePointTemplateDocumentsLibraryUrl(arg0: string) {
		this.SharePointTemplateDocumentsLibraryUrl = arg0;
	}

	setOutlookFilingTagTemplate(arg0: string) {
		this.OutlookFilingTagTemplate = arg0;
	}
	setOutlookIncludeInSearchResults(arg0: boolean) {
		this.OutlookIncludeInSearchResults = arg0;
	}
	setOutlookEnableCreateNewOption(arg0: boolean) {
		this.OutlookEnableCreateNewOption = arg0;
	}
	setOutlookEnableViewAllOption(arg0: boolean) {
		this.OutlookEnableViewAllOption = arg0;
	}

	setShowDeleteConfirmationDialog(arg0: boolean): any {
		this.showDeleteConfirmationDialog = arg0;
	}
	
	public async loadTokens(listUrl: string) {
		if(listUrl) {
			this.areTokensLoading = true;
			try {
				let api = await this.getApi();
				this.tokens = await api.getListTokens(listUrl);
				this.profiles.setTokens(this.tokens);
			} catch {
				// Do nothing on error
			} finally {
				this.areTokensLoading = false;
			}
		}
	}

	public async saveEntity(): Promise<void> {
		this.isSaving = true;
		try {

			//Save all the profiles
			await this.profiles.saveAll();

			let upsert:IEntityTypeDefinition = {
				DisplayTitle: '',
				ServerRelativeUrl: '',
				NativeMetadataUrl: '',
				Id: this.Id,
				SingularName: this.SingularName,
				PluralName: this.PluralName,
				Type: this.Type,
				SharePointListUrl: this.SharePointListUrl,
				SharePointStorageUrlField: this.SharePointStorageUrlField,
				SharePointStorageUrlPattern: this.SharePointStorageUrlPattern,
				SharePointTemplateDocumentsLibraryUrl: this.SharePointTemplateDocumentsLibraryUrl,
				ProvisionProfiles: this.profiles.profiles.map(p => p.Id),
				EnableWorkspaceSync: this.EnableWorkspaceSync,
				OutlookFilingTagTemplate: this.OutlookFilingTagTemplate,
				OutlookIncludeInSearchResults: this.OutlookIncludeInSearchResults,
				OutlookEnableCreateNewOption: this.OutlookEnableCreateNewOption,
				OutlookEnableViewAllOption: this.OutlookEnableViewAllOption,
				SharePointTemplateApprovalsListUrl: this.SharePointTemplateApprovalsListUrl,
				SharePointTemplateApprovals: this.SharePointTemplateApprovals
			};

			let api = await this.getApi();
			
			try
			{
				let id = await api.upsert(upsert, []);
				this.setId(id);
				this.testDialog.setEntityTypeId(id);
			} catch(e) {
				
				alert("Failed to save entity type");
			}
		} finally {
			this.isSaving = false;
		}
	}

	setId(id: string) {
		this.Id = id;
	}
	
	public loadList = async (listUrl: string) => {
		
		this.setListUrlColumns(undefined);
		this.setAvailableSiteTemplates([]);
		this.setAvailableContentTypes([]);
		this.setSensitivityLabels([]);

		if(listUrl) {
			
			this.loadTokens(listUrl);

			try {
				this.isLoadingList = true;
				let api = await this.getApi();
				this.setListUrlColumns(await api.getListFields(listUrl));

				//Sensitivity labels permission may not be granted
				try {
					this.setSensitivityLabels(await api.getSensitivityLabels());
				} catch(sensitivityLabelError) { 
					console.log(sensitivityLabelError);
					this.setSensitivityLabels([]);
				}

				let siteTemplates = await api.getSiteTemplates(1033, listUrl);
				siteTemplates.sort((a,b) => a.title > b.title ? 1 : -1);

				this.setAvailableSiteTemplates(siteTemplates);

				try {
					//Load content hub columns
					let hubContentTypes = await api.getContentHubTypes(listUrl);
					this.setAvailableContentTypes(hubContentTypes);
				} catch(contentTypeError) {
					console.log(contentTypeError);
					this.setAvailableContentTypes([]);
				}

				this.disabledTabs = [];
			} catch(e) {

				console.log(e);

				// List could not be found/accessed
				this.setListUrlColumns(undefined);
				this.setAvailableContentTypes(undefined);
				this.setAvailableSiteTemplates(undefined);
				this.setSensitivityLabels(undefined);
				
				//If we can't access the list, disable the provisioning and OneDrive tabs
				this.disabledTabs = ['Provisioning', 'OneDrive'];
			} finally {
				this.isLoadingList = false;
			}
		}
		
		return this.listUrlColumns;
	};

	public setAvailableSiteTemplates(siteTemplates?: ISiteTemplate[]) {
		this.availableSiteTemplates = siteTemplates;
		if(siteTemplates) {

			//Order them
			siteTemplates.sort((a,b) => a.title > b.title ? 1 : -1);
			this.profiles.setAvailableSiteTemplates(siteTemplates);
		}
	}

	setSensitivityLabels(arg0?: ISensitivityLabel[]) {
		if(arg0){
			this.sensitivityLabels = arg0;
			this.profiles.setSensitivityLabels(arg0);
		} else {
			this.sensitivityLabels = [];
		}
	}
	

	public setAvailableContentTypes(contentTypes?: IContentHubType[]) {

		if(contentTypes) {
			this.availableContentTypes = contentTypes;
			this.profiles.setAvailableContentTypes(contentTypes);

			
			function onlyUnique(value:string, index:number, array:string[]) {
				return array.indexOf(value) === index;
			}

			//Get all the columns from all content types
			let columns = contentTypes.map(c => c.columns).flat().map(c => c.name).filter(onlyUnique).sort((a,b) => { return a > b ? 1 : -1 });

			this.profiles.setAvailableContentHubColumns(columns);

		} else {
			this.availableContentTypes = undefined;
		}
	}

	public setListUrlColumns(columns?: IEntityColumn[]) {
		this.listUrlColumns = columns;
		if(columns) {
			this.profiles.setAvailableFields(columns);
		}
	}

	public canSave(): boolean {
		return (
			!!this.PluralName &&
			!!this.SingularName &&
			!!this.SharePointListUrl &&
			!!this.listUrlColumns
		);
	}
	
	public async deleteEntity(): Promise<void> {
		this.isDeleting = true;
		try {
			let api = await this.getApi();
			
			if(this.Id) {
				await api.delete([this.Id]);
			}

			this.onDelete();
		} finally {
			this.isDeleting = false;
		}
	}
}

function EntityTypeEditorDialog({
	state
}: {
	state: EntityTypeEditorState;
}) {
	useEffect(() => {
		state.loadList(state.SharePointListUrl);
	}, [state]);


	return (
		<>
			<Dialog
				hidden={!state.showDeleteConfirmationDialog}
				onDismiss={() => state.setShowDeleteConfirmationDialog(false)}
				dialogContentProps={{
					type: DialogType.normal,
					title: 'Delete',
					subText: `Are you sure you want to delete the list connection for '${state.PluralName}'? This does not delete any data, but all workspace automation will stop working.`,
				}}
				modalProps={{
					isBlocking: false,
				}}
			>
				<DialogFooter>
					<PrimaryButton
						onClick={() => {
							state.setShowDeleteConfirmationDialog(false);
							state.deleteEntity();
						}}
						text="Delete"
					/>
					<DefaultButton
						onClick={() => state.setShowDeleteConfirmationDialog(false)}
						text="Don't delete"
					/>
				</DialogFooter>
			</Dialog>

			<Stack horizontal>

				<StackItem grow style={{marginRight:20}}>
					<ProvisionProfiles state={state.profiles} />
				</StackItem>

				<StackItem style={{width:350}}>
					<Stack>

						<WidgetOutline state={state.connectionInfoOutline}>
							<EntityTypeConnectionInfo state={state} />
						</WidgetOutline>

						<WidgetOutline state={state.entityTypeSetupOutline}>
							<EntityTypeSetUp state={state} />
						</WidgetOutline>

						<WidgetOutline state={state.outlookSettingsOutline}>
							<EntityTypeOutlookSettings state={state} />
						</WidgetOutline>

						<WidgetOutline state={state.testDialogOutline}>
							<EntityTypeTestDialog state={state.testDialog} />
						</WidgetOutline>

						<Stack horizontal horizontalAlign='start'>
							<StackItem>
								<DefaultButton iconProps={{ iconName: `Save`, }}
										onClick={(ev) => { state.saveEntity(); }}
										disabled={!state.canSave() || state.isDeleting || state.isSaving }>
									Save settings
								</DefaultButton>
							</StackItem>
							
							<StackItem>
								<DefaultButton style={{marginLeft:10}} text="⚙️" menuProps={{items:[
									{ key: 'newprofile', text: 'Add new profile', onClick: () => {state.profiles.addNewProfile();}, iconProps:{iconName:'Add'} },
									{ key: 'removeprofile', text: 'Remove profile', onClick: () => {state.profiles.removeProfile();}, disabled: state.profiles.profiles.length <= 1, iconProps:{iconName:'Delete'} },
									{ key: 'delete', text: `Delete '${state.PluralName}' connection`, onClick: () => {state.setShowDeleteConfirmationDialog(true);}, disabled:(state.isDeleting || state.isSaving), iconProps:{iconName:'Delete'} },
								]}} />
							</StackItem>

						</Stack>

						{state.isSaving || state.isDeleting ? <>
							<Spinner label={state.isSaving ? 'Saving...' : state.isDeleting ? 'Deleting...' : ''} />
						</> : <></> }

					</Stack>
				</StackItem>

			</Stack>
		
		</>
	);
}

export default observer(EntityTypeEditorDialog);
